import { useCallback, useRef } from "react";
import { ColumnDef, SpreadsheetCell } from "@components/spreadsheet/types";
import { AccrualImporterRow, AccrualMode } from "./types";
import { isFirstRowInGroup } from "./useAccrualImporterState";
import {
    pickLatestInvoiceNumber,
    suggestNextInvoiceNumber,
} from "./suggestInvoiceNumber";

interface UsePrefillInvoiceNumberParams {
    mode: AccrualMode;
    columns: ColumnDef[];
    setRows: React.Dispatch<React.SetStateAction<AccrualImporterRow[]>>;
    lastServerInvoiceNumber: string | null | undefined;
    isLastServerNumberReady: boolean;
}

export function usePrefillInvoiceNumber({
    mode,
    columns,
    setRows,
    lastServerInvoiceNumber,
    isLastServerNumberReady,
}: UsePrefillInvoiceNumberParams) {
    const lastCellRef = useRef<SpreadsheetCell | null>(null);

    return useCallback(
        (cell: SpreadsheetCell | null) => {
            if (!cell) {
                lastCellRef.current = null;
                return;
            }

            const col = columns[cell.col];
            if (!col || col.field !== "invoiceNumber") {
                return;
            }

            if (!isLastServerNumberReady) {
                return;
            }

            const prev = lastCellRef.current;
            if (prev && prev.row === cell.row && prev.col === cell.col) {
                return;
            }
            lastCellRef.current = cell;

            setRows((prevRows) => {
                if (cell.row >= prevRows.length) {
                    return prevRows;
                }

                const row = prevRows[cell.row];
                if (row.invoiceNumber) {
                    return prevRows;
                }
                if (!isFirstRowInGroup(prevRows, cell.row)) {
                    return prevRows;
                }

                const sessionNumbers = prevRows.map((r) => r.invoiceNumber);
                const latest = pickLatestInvoiceNumber([
                    lastServerInvoiceNumber,
                    ...sessionNumbers,
                ]);
                const suggestion = suggestNextInvoiceNumber(latest, mode);

                const next = [...prevRows];
                next[cell.row] = {
                    ...row,
                    invoiceNumber: suggestion,
                };
                return next;
            });
        },
        [
            columns,
            mode,
            setRows,
            lastServerInvoiceNumber,
            isLastServerNumberReady,
        ],
    );
}
